import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  MdMenu, MdClose, MdChevronRight, MdExpandMore, MdSearch,
  MdArrowBackIos, MdArrowForwardIos, MdLanguage, MdKeyboardArrowUp,
} from 'react-icons/md';
import axiosInstance from '../utils/axiosInstance';
import Loader from '../components/Loader';
import theme from '../styles/theme';

const MOBILE_QUERY = '(max-width: 900px)';

/* ─── Layout shell ───────────────────────────────────────────────────────── */

const Shell = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #f8fafc;
`;

const Overlay = styled.div`
  display: none;
  @media ${MOBILE_QUERY} {
    display: ${(p) => (p.$open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    z-index: 40;
    background: rgba(15, 23, 42, 0.5);
  }
`;

const Sidebar = styled.aside`
  width: 320px;
  flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;

  @media ${MOBILE_QUERY} {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 45;
    transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
    transition: transform 0.22s ease;
    box-shadow: ${(p) => (p.$open ? '0 0 0 100vmax rgba(0,0,0,0)' : 'none')};
  }
`;

const SidebarHeader = styled.div`
  padding: 18px 16px 14px;
  border-bottom: 1px solid #e5e7eb;
`;

const CourseName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CourseMeta = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const SearchBox = styled.div`
  margin: 12px 16px 4px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 28px 8px 32px;
  border-radius: 9px;
  border: 1px solid #e5e7eb;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${theme.primary}; }
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
`;

const SearchKbd = styled.kbd`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  pointer-events: none;
`;

const TreeScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 8px 24px;
`;

const CategoryHeader = styled.button`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 9px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #4338ca;
  cursor: pointer;
  text-align: left;
  &:hover { background: #f5f3ff; }

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MainTitleLabel = styled.div`
  margin: 6px 0 2px 22px;
  padding-right: 8px;
  font-size: 12.5px;
  font-weight: 700;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LessonLink = styled.button`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: ${(p) => (p.$active ? theme.primaryLight : 'transparent')};
  color: ${(p) => (p.$active ? theme.primaryDarker : '#4b5563')};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  padding: 8px 10px 8px 34px;
  border-radius: 8px;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  line-height: 1.35;
  &:hover { background: ${(p) => (p.$active ? theme.primaryLight : '#f3f4f6')}; }
`;

const LessonDot = styled.span`
  flex-shrink: 0;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? theme.primary : '#cbd5e1')};
`;

const LessonLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptySidebarNote = styled.p`
  padding: 24px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
`;

/* ─── Main pane ──────────────────────────────────────────────────────────── */

const Main = styled.main`
  flex: 1;
  min-width: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 9px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #374151;
  flex-shrink: 0;
  &:hover { background: #f9fafb; }
`;

const MenuToggle = styled(IconButton)`
  display: none;
  @media ${MOBILE_QUERY} { display: inline-flex; }
`;

const Breadcrumb = styled.div`
  min-width: 0;
  flex: 1;
  font-size: 12.5px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProgressBadge = styled.div`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #4338ca;
  background: ${theme.primaryLight};
  border-radius: 999px;
  padding: 5px 12px;
  white-space: nowrap;
`;

const LangSwitch = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 2px;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 2px;
`;

const LangButton = styled.button`
  border: none;
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none')};
  color: ${(p) => (p.$active ? '#111827' : '#6b7280')};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ArticleScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`;

const ArticleWrap = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 36px 40px 80px;
  @media (max-width: 768px) { padding: 22px 16px 60px; }
`;

const ScrollTopButton = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 25;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: ${theme.gradient};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(67, 56, 202, 0.35);
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? '0' : '12px')});
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transition: opacity 0.18s ease, transform 0.18s ease;
  &:hover { opacity: ${(p) => (p.$visible ? 0.9 : 0)}; }

  @media ${MOBILE_QUERY} {
    right: 16px;
    bottom: 16px;
  }
`;

const ArticleBody = styled.div`
  font-size: 16px;
  line-height: 1.8;
  color: #1f2937;

  h1 { font-size: 26px; font-weight: 700; margin: 32px 0 14px; color: #111827; }
  h2 { font-size: 22px; font-weight: 700; margin: 28px 0 12px; color: #111827; }
  h3 { font-size: 19px; font-weight: 700; margin: 24px 0 10px; color: #111827; }
  h4 { font-size: 17px; font-weight: 700; margin: 20px 0 8px; color: #111827; }
  p { margin: 0 0 16px; }
  ul, ol { margin: 0 0 16px; padding-left: 24px; }
  li { margin-bottom: 6px; }
  a { color: ${theme.primary}; text-decoration: underline; }
  blockquote {
    border-left: 4px solid ${theme.primary};
    background: ${theme.primaryLight};
    color: ${theme.primaryDarker};
    padding: 10px 18px;
    margin: 16px 0;
    border-radius: 0 10px 10px 0;
  }
  pre {
    background: #0f172a;
    color: #e2e8f0;
    padding: 16px;
    border-radius: 12px;
    overflow-x: auto;
    font-size: 13.5px;
    margin: 16px 0;
  }
  code { font-family: 'SFMono-Regular', Consolas, monospace; }
  img { max-width: 100%; border-radius: 12px; margin: 16px 0; }
  hr { border: none; border-top: 1px solid #e5e7eb; margin: 28px 0; }
  table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
  td, th { border: 1px solid #e5e7eb; padding: 8px 10px; }
  iframe { max-width: 100%; border-radius: 12px; }
`;

const EmptyArticle = styled.div`
  color: #9ca3af;
  font-size: 14px;
  padding: 8px 0;
  font-style: italic;
`;

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 48px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  max-width: 48%;
  &:hover { border-color: ${theme.primary}; color: ${theme.primary}; }
  &:disabled { opacity: 0.35; cursor: not-allowed; pointer-events: none; }
`;

const NavButtonText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CenteredMessage = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  color: #6b7280;
  font-size: 14px;
`;

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function flattenLessons(tree) {
  const flat = [];
  (tree || []).forEach((cat) => {
    (cat.mainTitles || []).forEach((mt) => {
      (mt.subTitles || []).forEach((st) => {
        flat.push({ cat, mt, st });
      });
    });
  });
  return flat;
}

// Converts lesson HTML into plain lowercase text once per lesson, so the
// search box can match against the actual written content, not just titles.
function stripHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').toLowerCase();
}

function buildSearchIndex(flatLessons) {
  const index = new Map();
  flatLessons.forEach(({ st }) => {
    const text = [st.title, stripHtml(st.content?.en), stripHtml(st.content?.ta)]
      .join(' ')
      .toLowerCase();
    index.set(st.id, text);
  });
  return index;
}

function filterTree(tree, query, searchIndex) {
  const q = query.trim().toLowerCase();
  if (!q) return tree || [];
  return (tree || [])
    .map((cat) => {
      const catMatches = cat.name.toLowerCase().includes(q);
      const mainTitles = (cat.mainTitles || [])
        .map((mt) => {
          const mtMatches = mt.title.toLowerCase().includes(q);
          const subTitles = (mt.subTitles || []).filter((st) => {
            if (catMatches || mtMatches) return true;
            const indexed = searchIndex?.get(st.id);
            return indexed ? indexed.includes(q) : st.title.toLowerCase().includes(q);
          });
          return subTitles.length > 0 ? { ...mt, subTitles } : null;
        })
        .filter(Boolean);
      return mainTitles.length > 0 ? { ...cat, mainTitles } : null;
    })
    .filter(Boolean);
}

/* ─── Component ──────────────────────────────────────────────────────────── */

export default function SharedCourseViewer() {
  const { courseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const batchId = searchParams.get('batchId');

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeLessonId, setActiveLessonId] = useState(null);
  const [expandedCatId, setExpandedCatId] = useState(null);
  const [search, setSearch] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lang, setLang] = useState('en');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const articleRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleArticleScroll = (e) => {
    setShowScrollTop(e.target.scrollTop > 400);
  };

  const scrollToTop = () => {
    articleRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // "/" focuses the course search box from anywhere on the page (unless
  // already typing in another field), matching the shortcut hint shown in
  // its placeholder.
  useEffect(() => {
    const handleSlash = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    document.addEventListener('keydown', handleSlash);
    return () => document.removeEventListener('keydown', handleSlash);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await axiosInstance.get(`/students/shared-courses/${courseId}`, {
          params: { batchId },
        });
        if (cancelled) return;
        setCourse(data);
        const flat = flattenLessons(data.tree);
        if (flat.length > 0) {
          const lessonParam = searchParams.get('lesson');
          const match = lessonParam ? flat.find((l) => l.st.id === lessonParam) : null;
          const initial = match || flat[0];
          setActiveLessonId(initial.st.id);
          setExpandedCatId(initial.cat.id);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Failed to load this course.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Only re-run when the course/batch changes — searchParams is read once
    // here for the initial deep link, not tracked afterward.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, batchId]);

  const flatLessons = useMemo(() => flattenLessons(course?.tree), [course]);
  const activeIndex = useMemo(
    () => flatLessons.findIndex((l) => l.st.id === activeLessonId),
    [flatLessons, activeLessonId]
  );
  const active = activeIndex >= 0 ? flatLessons[activeIndex] : null;
  const prevLesson = activeIndex > 0 ? flatLessons[activeIndex - 1] : null;
  const nextLesson = activeIndex >= 0 && activeIndex < flatLessons.length - 1 ? flatLessons[activeIndex + 1] : null;

  const searchIndex = useMemo(() => buildSearchIndex(flatLessons), [flatLessons]);
  const visibleTree = useMemo(() => filterTree(course?.tree, search, searchIndex), [course, search, searchIndex]);

  const hasEnglish = Boolean(active?.st.content?.en?.trim());
  const hasTamil = Boolean(active?.st.content?.ta?.trim());
  const effectiveLang = lang === 'ta' && hasTamil ? 'ta' : hasEnglish ? 'en' : (hasTamil ? 'ta' : 'en');

  const selectLesson = (catId, lessonId) => {
    setActiveLessonId(lessonId);
    setExpandedCatId(catId);
    setSidebarOpen(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('lesson', lessonId);
      return next;
    }, { replace: true });
    articleRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <Loader />;

  if (error) {
    return <CenteredMessage>{error}</CenteredMessage>;
  }

  if (!course || flatLessons.length === 0) {
    return <CenteredMessage>This course doesn't have any published lessons yet.</CenteredMessage>;
  }

  return (
    <Shell>
      <Overlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      <Sidebar $open={sidebarOpen}>
        <SidebarHeader>
          <CourseName>{course.title}</CourseName>
          <CourseMeta>{flatLessons.length} {flatLessons.length === 1 ? 'lesson' : 'lessons'}</CourseMeta>
        </SidebarHeader>

        <SearchBox>
          <SearchIcon size={16} />
          <SearchInput
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entire course… ( / )"
          />
          {!search && <SearchKbd>/</SearchKbd>}
        </SearchBox>

        <TreeScroll>
          {visibleTree.length === 0 ? (
            <EmptySidebarNote>No lessons match your search.</EmptySidebarNote>
          ) : (
            visibleTree.map((cat) => {
              const expanded = search.trim() ? true : expandedCatId === cat.id;
              return (
                <div key={cat.id}>
                  <CategoryHeader onClick={() => setExpandedCatId(expanded ? null : cat.id)}>
                    {expanded ? <MdExpandMore size={16} /> : <MdChevronRight size={16} />}
                    <span>{cat.name}</span>
                  </CategoryHeader>
                  {expanded && cat.mainTitles.map((mt) => (
                    <div key={mt.id}>
                      <MainTitleLabel>{mt.title}</MainTitleLabel>
                      {mt.subTitles.map((st) => {
                        const isActive = st.id === activeLessonId;
                        return (
                          <LessonLink key={st.id} $active={isActive} onClick={() => selectLesson(cat.id, st.id)}>
                            <LessonDot $active={isActive} />
                            <LessonLabel>{st.title}</LessonLabel>
                          </LessonLink>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </TreeScroll>
      </Sidebar>

      <Main>
        <TopBar>
          <MenuToggle onClick={() => setSidebarOpen(true)} title="Table of contents">
            <MdMenu size={18} />
          </MenuToggle>
          <Breadcrumb>{active ? `${active.cat.name} / ${active.mt.title} / ${active.st.title}` : course.title}</Breadcrumb>
          {(hasEnglish && hasTamil) && (
            <LangSwitch>
              <LangButton $active={effectiveLang === 'en'} onClick={() => setLang('en')}>
                <MdLanguage size={13} /> EN
              </LangButton>
              <LangButton $active={effectiveLang === 'ta'} onClick={() => setLang('ta')}>
                <MdLanguage size={13} /> TA
              </LangButton>
            </LangSwitch>
          )}
          <ProgressBadge>{activeIndex + 1} / {flatLessons.length}</ProgressBadge>
          <IconButton title="Close tab" onClick={() => window.close()}>
            <MdClose size={18} />
          </IconButton>
        </TopBar>

        <ArticleScroll ref={articleRef} onScroll={handleArticleScroll}>
          <ArticleWrap>
            {active && (
              <>
                {active.st.content?.[effectiveLang]?.trim() ? (
                  <ArticleBody dangerouslySetInnerHTML={{ __html: active.st.content[effectiveLang] }} />
                ) : (
                  <EmptyArticle>Content for this lesson hasn't been added yet.</EmptyArticle>
                )}

                <NavRow>
                  <NavButton disabled={!prevLesson} onClick={() => prevLesson && selectLesson(prevLesson.cat.id, prevLesson.st.id)}>
                    <MdArrowBackIos size={12} />
                    <NavButtonText>{prevLesson ? prevLesson.st.title : 'No previous lesson'}</NavButtonText>
                  </NavButton>
                  <NavButton disabled={!nextLesson} onClick={() => nextLesson && selectLesson(nextLesson.cat.id, nextLesson.st.id)} style={{ textAlign: 'right', justifyContent: 'flex-end' }}>
                    <NavButtonText>{nextLesson ? nextLesson.st.title : 'No next lesson'}</NavButtonText>
                    <MdArrowForwardIos size={12} />
                  </NavButton>
                </NavRow>
              </>
            )}
          </ArticleWrap>
        </ArticleScroll>

        <ScrollTopButton $visible={showScrollTop} onClick={scrollToTop} title="Back to top">
          <MdKeyboardArrowUp size={22} />
        </ScrollTopButton>
      </Main>
    </Shell>
  );
}
